import { useState, useEffect } from 'react';
import api from '../services/api.js';

// Module-level cache so the fetch only happens once per page load,
// regardless of how many components call this hook.
//
// BUG-1 fix: never cache an error result. A single transient /me failure
// (network blip, 401 during MSAL refresh, brief 502 during deploy) used to
// permanently set _cache = { role: null } for the rest of the session,
// silently hiding all isAdmin-gated UI platform-wide. Now we null the
// cache + the in-flight promise so the next mount retries cleanly.
let _cache = null;
let _promise = null;

export function useCurrentUser() {
  const [user, setUser] = useState(_cache);
  // ADMINROUTE-COLD-LOAD-RACE fix: tri-state so callers can tell "not yet
  // known" apart from "known, and not an admin" instead of relying on the
  // ambiguous `role === null` check that never actually fired.
  const [authState, setAuthState] = useState(
    _cache ? (_cache.role === 'admin' ? 'admin' : 'not-admin') : 'loading'
  );

  useEffect(() => {
    if (_cache) {
      setUser(_cache);
      setAuthState(_cache.role === 'admin' ? 'admin' : 'not-admin');
      return;
    }
    if (!_promise) {
      _promise = api.get('/api/users/me')
        .then(r => { _cache = r.data; return _cache; })
        .catch(err => {
          console.warn('[useCurrentUser] /me failed — will retry on next mount:', err?.message ?? err);
          _cache   = null;
          _promise = null;
          return null;
        });
    }
    _promise.then(u => {
      setUser(u);
      setAuthState(u?.role === 'admin' ? 'admin' : 'not-admin');
    });
  }, []);

  const resolved = user ?? {};
  // ISADMIN-DEAD-GATE-1 fix: derive isAdmin here so every consumer that
  // already destructures it (the 9 live-gate pages) gets the real value
  // instead of permanently-undefined.
  //
  // USER-DEAD-GATE-1 fix (PLATFORM-FORMAT-REVERIFY-1 addendum, 2026-09-16):
  // same failure class, a different field. This hook has never returned a
  // `user` key -- only the flat spread of the API response (Id/Name/Email/
  // role/...) plus isAdmin/authState. Every call site written as
  // `const { user } = useCurrentUser()` (9 files: RecallRecords.jsx,
  // SecurityLogs.jsx, BudgetBuilder.jsx, FixedAssets.jsx,
  // PurchaseRequisitions.jsx, MrpScheduleGantt.jsx,
  // BatchWorkflowPanels.jsx, MagnetPullTest.jsx, MarketLandscape.jsx) has
  // been silently getting `user === undefined` forever -- confirmed live on
  // RecallRecords.jsx: `/api/users/me` correctly returns role:"admin",
  // the resulting `isAdmin` gate is correctly wired in the JSX, but
  // `user?.role` was reading a property off `undefined` and permanently
  // evaluating false, hiding the "+ New Record" header button for every
  // admin, indefinitely (proven not a timing race: still absent 5s after
  // networkidle). Fixed the same way ISADMIN-DEAD-GATE-1 was: add the
  // missing field here, once, rather than touching every call site --
  // `user` now aliases the same flat object every other field on this
  // return value already comes from.
  //
  // ROLES-DEAD-GATE-1 fix (2026-09-19, found while confirming
  // CustomerComplaints.jsx's "+ New Complaint" button for NAV-SECTION-FOLD-1):
  // same failure class, a THIRD field. `Users.Role` is a single scalar column
  // -- `/api/users/me` (backend/src/routes/users.js) has never selected a
  // `roles` array, there is no multi-role concept anywhere in this schema --
  // but 14 frontend files were written as `currentUser?.roles?.some(...)`,
  // permanently evaluating undefined regardless of actual role (confirmed:
  // CustomerComplaints.jsx's own "+ New Complaint" button never rendered for
  // anyone, on any tenant, since the file was written). Same fix, same place:
  // add the missing field once, as a 1-element array wrapping the real
  // `role`, rather than rewriting 14 call sites' `.some()` checks.
  return { ...resolved, user: resolved, roles: resolved.role ? [resolved.role] : [], isAdmin: resolved.role === 'admin', authState };
}
